import React from 'react';
import styled from 'styled-components';
import { MantraModal } from '../../../Components/Modal';
import { Text, colors } from '../../../globalStyles';
import { useSaiStudentDetailQuery } from '../../../graphQL';
import { AssessmentFlags } from '../../Users/Widgets/AssessmentFlags';
import { ComplexNeedsPill } from '../../../Components/Beacon/ComplexNeedsPill';
import {
  AssessmentIndexWidget,
  INDEX_CARDS_STACK_BELOW,
} from '../../Users/Widgets/AssessmentIndex/AssessmentIndexWidget';
import { LoadingWidget } from '../../Users/Widgets/Loading';
import { AssessmentInsightsCard } from '../../Users/Widgets/AssessmentInsights';
import { TriageCategoryBadge } from '../../Users/Widgets/TriageCategoryBadge/TriageCategoryBadge';
import { BeaconQueryError } from '../../Users/Widgets/BeaconQueryError';

type StudentDetailModalProps = {
  /** The scoring run the Students list row named. Rows without one do not open this modal. */
  surveyResultId: number;
  onClose: () => void;
};

export const StudentDetailModal = ({
  surveyResultId,
  onClose,
}: StudentDetailModalProps): React.ReactElement => {
  const { data, loading, error, refetch } = useSaiStudentDetailQuery({
    variables: { surveyResultId },
  });

  const detail = data?.saiStudentDetail ?? null;
  // The run names its own student, so the header and the clinical regions below describe one person.
  const student = detail?.student ?? null;
  const name = student?.preferredName?.trim() || null;
  const triage = detail?.triage ?? null;

  return (
    <WideModal isOpen onClose={onClose} size="l">
      <ModalContentDiv>
        <HeaderCard>
          {student && (
            <IdentityDiv>
              <StudentNameHeading className={name ? 'ttc' : undefined}>
                {name || student.email}
              </StudentNameHeading>
              {name && <Text.bodyGrey>{student.email}</Text.bodyGrey>}
              {detail?.hasComplexNeeds && (
                <div>
                  <ComplexNeedsPill />
                </div>
              )}
            </IdentityDiv>
          )}
          {detail && (
            <TriageSlotDiv>
              {triage ? (
                <TriageCategoryBadge triage={triage} />
              ) : (
                <EmptyTriageSpan>
                  {detail.indices || detail.flags.length
                    ? 'This assessment reached no triage category'
                    : 'This assessment produced no Beacon results'}
                </EmptyTriageSpan>
              )}
            </TriageSlotDiv>
          )}
        </HeaderCard>
        {loading && <LoadingWidget />}
        {!loading && error && (
          <BeaconQueryError error={error} onRetry={() => refetch().catch(() => {})} />
        )}
        {detail && (
          <>
            <AssessmentInsightsCard aiSummary={detail.aiSummary ?? null} />
            <AssessmentIndexWidget indices={detail.indices ?? null} flags={detail.flags} />
            <AssessmentFlags flags={detail.flags} hasComplexNeeds={detail.hasComplexNeeds} />
          </>
        )}
      </ModalContentDiv>
    </WideModal>
  );
};

const WideModal = styled(MantraModal)`
  max-width: calc(100vw - 220px - 64px);
  width: calc(100vw - 220px - 64px);
  max-height: 92vh;
  overflow-y: auto;
  border-radius: 4px;

  @media (max-width: 959px) {
    max-width: calc(100vw - 32px);
    width: calc(100vw - 32px);
  }
`;

const ModalContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderCard = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  box-sizing: border-box;
  padding: 18px 24px;
  background-color: ${colors.white};
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 12px;
`;

// The name block spans exactly one column of the index-card grid below (the header and the grid
// share edges and the same 20px gap), so the triage block lands on the second card's left edge.
const IdentityDiv = styled.div`
  display: flex;
  flex: 0 0 calc(50% - 10px);
  min-width: 0;
  flex-direction: column;
  gap: 6px;

  @media (max-width: ${INDEX_CARDS_STACK_BELOW}) {
    flex: 0 1 auto;
  }
`;

const StudentNameHeading = styled.h2`
  font-size: 1.75rem;
  font-weight: 400;
  line-height: 1.15;
  margin: 0;
`;

const TriageSlotDiv = styled.div`
  align-items: center;
  display: flex;
`;

const EmptyTriageSpan = styled.span`
  color: ${colors.grayText};
  font-size: 16px;
  font-weight: 600;
`;
